import { ApiError } from '../api';

/** Verwaltungs-Schnittstelle. Sitzung per Cookie, Änderungen mit dem Schutz-Header gegen CSRF. */

export interface Identity {
  user: { email: string; name: string | null; role: string };
  project: { id: string; slug: string; name: string };
}

export interface WebinarSummary {
  id: string;
  slug: string;
  title: string;
  status: 'draft' | 'active' | 'archived';
  durationSeconds: number;
  registrations: number;
  openQuestions: number;
  video: { status: string; progressPercent: number } | null;
  createdAt: string;
}

export interface Question {
  id: string;
  text: string;
  positionSeconds: number;
  askedAt: string;
  answer: string | null;
  answeredAt: string | null;
  name: string | null;
  email: string;
  sessionStartsAt: string;
  mailStatus: 'queued' | 'sent' | 'failed' | 'cancelled' | 'skipped' | null;
}

export type ScheduleRule = { kind: 'recurring'; weekdays: number[]; times: string[] } | { kind: 'next_round'; roundTo: 'hour' | 'half_hour' };

export interface WebinarDetail {
  id: string;
  slug: string;
  title: string;
  status: 'draft' | 'active' | 'archived';
  durationSeconds: number;
  attendanceThresholdPercent: number;
  lateEntryCutoffSeconds: number | null;
  cta: { offsetSeconds: number | null; headline: string | null; text: string | null; buttonLabel: string | null; url: string | null; deadlineHours: number | null };
  followup: { enabled: boolean; delayMinutes: number };
  replayValidHours: number | null;
  fullscreenEnabled: boolean;
  schedules: (ScheduleRule & { id: string })[];
  hints: { id: string; offsetSeconds: number; text: string }[];
  video: { id: string; status: string; progressPercent: number; durationSeconds: number | null; width: number | null; height: number | null; warnings: string[]; rejectionCode: string | null; originalFilename: string } | null;
  waitingImageUrl: string | null;
  registrations: number;
  beyondEnd: { cta: boolean; hints: number[] };
}

export interface WebinarPatch {
  title?: string;
  slug?: string;
  status?: 'draft' | 'active' | 'archived';
  durationSeconds?: number;
  attendanceThresholdPercent?: number;
  lateEntryCutoffSeconds?: number | null;
  ctaOffsetSeconds?: number | null;
  ctaHeadline?: string | null;
  ctaText?: string | null;
  ctaButtonLabel?: string | null;
  ctaUrl?: string | null;
  ctaDeadlineHours?: number | null;
  followupEnabled?: boolean;
  followupDelayMinutes?: number;
  replayValidHours?: number | null;
  fullscreenEnabled?: boolean;
}

export interface Registration {
  id: string;
  email: string;
  name: string | null;
  sessionStartsAt: string;
  createdAt: string;
  cancelledAt: string | null;
  watchedSeconds: number;
  attended: boolean | null;
  ctaClicked: boolean;
}

export interface WebinarStats {
  durationSeconds: number;
  attendanceThresholdPercent: number;
  registrations: { total: number; cancelled: number; upcoming: number; started: number };
  attended: number;
  noShows: number;
  showRatePercent: number | null;
  averageWatchedSeconds: number | null;
  watchCurve: number[];
  cta: { offsetSeconds: number | null; clicks: number; registrations: number; byMinute: { minute: number; clicks: number }[] };
  questions: { total: number; open: number };
}

export interface ProjectSettings {
  slug: string;
  name: string;
  timezone: string;
  customDomain: string | null;
  senderEmail: string | null;
  senderName: string | null;
  vacationFrom: string | null;
  vacationUntil: string | null;
  theme: { scheme?: 'light' | 'dark'; brand?: string; action?: string; font?: 'sans' | 'serif'; logoUrl?: string };
  logoPath: string | null;
  brevoListId: number | null;
  newsletterOffered: boolean;
  brevoAvailable: boolean;
  retentionDays: number;
}

export interface MailTemplateView {
  kind: string;
  label: string;
  when: string;
  required: string[];
  subject: string;
  body: string;
  source: 'default' | 'project' | 'webinar';
  custom: boolean;
  updatedAt: string | null;
}

/** Ein Bild roh hochladen – der Server erkennt das Format an den Bytes. */
async function upload<T>(path: string, file: File): Promise<T> {
  const res = await fetch(`/api/admin${path}`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'X-Requested-With': 'fetch', 'Content-Type': file.type || 'image/jpeg' },
    body: file,
  });
  if (!res.ok) {
    let message = '';
    try {
      message = ((await res.json()) as { message?: string }).message ?? '';
    } catch {
      /* kein JSON */
    }
    throw new ApiError(res.status, message || (res.status === 413 ? 'Das Bild ist größer als 5 MB.' : res.status === 415 ? 'Bitte ein JPG, PNG oder WebP hochladen.' : String(res.status)));
  }
  return (await res.json()) as T;
}

async function call<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`/api/admin${path}`, {
    method: init.method ?? 'GET',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'fetch',
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  if (!res.ok) {
    let code = String(res.status);
    let message = '';
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      code = body.error ?? code;
      message = body.message ?? '';
    } catch {
      /* kein JSON */
    }
    throw new ApiError(res.status, message || code);
  }
  return (await res.json()) as T;
}

export const adminApi = {
  me: () => call<Identity>('/me'),
  login: (email: string, password: string) => call<Identity>('/login', { method: 'POST', body: { email, password } }),
  logout: () => call<{ ok: true }>('/logout', { method: 'POST' }),
  changePassword: (currentPassword: string, newPassword: string) => call<{ ok: true }>('/password', { method: 'POST', body: { currentPassword, newPassword } }),
  listWebinars: () => call<{ webinars: WebinarSummary[] }>('/webinars'),
  createWebinar: (title: string) => call<{ id: string }>('/webinars', { method: 'POST', body: { title } }),
  getWebinar: (id: string) => call<WebinarDetail>(`/webinars/${id}`),
  updateWebinar: (id: string, patch: WebinarPatch) => call<WebinarDetail>(`/webinars/${id}`, { method: 'PATCH', body: patch }),
  putSchedules: (id: string, schedules: ScheduleRule[]) => call<{ ok: true }>(`/webinars/${id}/schedules`, { method: 'PUT', body: { schedules } }),
  putHints: (id: string, hints: { offsetSeconds: number; text: string }[]) => call<{ beyondEnd: number[] }>(`/webinars/${id}/hints`, { method: 'PUT', body: { hints } }),
  listRegistrations: (id: string) => call<{ registrations: Registration[] }>(`/webinars/${id}/registrations`),
  getStats: (id: string) => call<WebinarStats>(`/webinars/${id}/stats`),
  registrationsCsvUrl: (id: string) => `/api/admin/webinars/${id}/registrations.csv`,
  listQuestions: (id: string) => call<{ questions: Question[] }>(`/webinars/${id}/questions`),
  answerQuestion: (questionId: string, answer: string) => call<Question>(`/questions/${questionId}/answer`, { method: 'POST', body: { answer } }),
  listMails: (webinarId?: string) => call<{ mails: MailTemplateView[]; placeholders: { key: string; description: string }[] }>(webinarId ? `/webinars/${webinarId}/mails` : '/mails'),
  saveMail: (kind: string, input: { subject: string; body: string }, webinarId?: string) => call<MailTemplateView>(webinarId ? `/webinars/${webinarId}/mails/${kind}` : `/mails/${kind}`, { method: 'PUT', body: input }),
  resetMail: (kind: string, webinarId?: string) => call<MailTemplateView>(webinarId ? `/webinars/${webinarId}/mails/${kind}` : `/mails/${kind}`, { method: 'DELETE' }),
  previewMail: (kind: string, input: { subject: string; body: string }) => call<{ subject: string; text: string; html: string }>(`/mails/${kind}/preview`, { method: 'POST', body: input }),
  getProject: () => call<ProjectSettings>('/project'),
  updateProject: (patch: Partial<ProjectSettings>) => call<ProjectSettings>('/project', { method: 'PATCH', body: patch }),
  createBrevoList: () => call<ProjectSettings>('/project/brevo-list', { method: 'POST' }),
  uploadLogo: (file: File) => upload<{ logoPath: string }>('/project/logo', file),
  removeLogo: () => call<{ logoPath: null }>('/project/logo', { method: 'DELETE' }),
  uploadWaitingImage: (id: string, file: File) => upload<{ waitingImageUrl: string }>(`/webinars/${id}/waiting-image`, file),
  removeWaitingImage: (id: string) => call<{ waitingImageUrl: null }>(`/webinars/${id}/waiting-image`, { method: 'DELETE' }),
};

export function errorText(error: unknown, fallback = 'Das hat nicht geklappt. Bitte versuch es noch einmal.'): string {
  if (error instanceof ApiError && error.code && !/^\d+$/.test(error.code)) return error.code;
  return fallback;
}
